import { defineMessages } from '../../i18n/messages';
import { englishText } from '../../utils/messageFallbacks';

const PRICING_TEASER_DEFINITIONS = {
  TITLE: {
    id: 'homepage.pricing_teaser.title',
    text: 'Simple, Predictable Pricing',
    description: 'Section heading for the pricing teaser section',
  },
  SUBTITLE: {
    id: 'homepage.pricing_teaser.subtitle',
    text: 'Same features in every plan. Pick the word allowance that fits.',
    description: 'Subtitle below the pricing teaser heading. Explains that the plans differ only by how many words they include, not by feature set.',
  },
  FEATURES_LABEL: {
    id: 'homepage.pricing_teaser.features_label',
    text: 'In every plan',
    description: 'Small label above the feature list inside each plan card, making clear that the four items below are identical across all plans.',
  },
  PLAN_TRIAL_PRICE: {
    id: 'homepage.pricing_teaser.plan_trial_price',
    text: '$0',
    description: 'Trial plan price',
  },
  PLAN_TRIAL_PERIOD: {
    id: 'homepage.pricing_teaser.plan_trial_period',
    text: 'forever',
    description: 'Trial plan period',
  },
  PLAN_TRIAL_WORDS: {
    id: 'homepage.pricing_teaser.plan_trial_words',
    text: '5,000 words included',
    description: 'Trial plan word quota',
  },
  PLAN_STARTER_PRICE: {
    id: 'homepage.pricing_teaser.plan_starter_price',
    text: '$9',
    description: 'Starter plan monthly price',
  },
  PLAN_STARTER_PERIOD: {
    id: 'homepage.pricing_teaser.plan_starter_period',
    text: '/mo · billed annually',
    description: 'Starter plan billing period',
  },
  PLAN_STARTER_WORDS: {
    id: 'homepage.pricing_teaser.plan_starter_words',
    text: '50,000 words/year',
    description: 'Starter plan yearly word quota',
  },
  PLAN_GROWTH_PRICE: {
    id: 'homepage.pricing_teaser.plan_growth_price',
    text: '$29',
    description: 'Growth plan monthly price',
  },
  PLAN_GROWTH_PERIOD: {
    id: 'homepage.pricing_teaser.plan_growth_period',
    text: '/mo · billed annually',
    description: 'Growth plan billing period',
  },
  PLAN_GROWTH_WORDS: {
    id: 'homepage.pricing_teaser.plan_growth_words',
    text: '250,000 words/year',
    description: 'Growth plan yearly word quota',
  },
  PLAN_SCALE_PRICE: {
    id: 'homepage.pricing_teaser.plan_scale_price',
    text: '$79',
    description: 'Scale plan monthly price',
  },
  PLAN_SCALE_PERIOD: {
    id: 'homepage.pricing_teaser.plan_scale_period',
    text: '/mo · billed annually',
    description: 'Scale plan billing period',
  },
  PLAN_SCALE_WORDS: {
    id: 'homepage.pricing_teaser.plan_scale_words',
    text: '1,000,000 words/year',
    description: 'Scale plan yearly word quota',
  },
  FEATURE_LOCALES: {
    id: 'homepage.pricing_teaser.feature_locales',
    text: '56 locales',
    description: 'Feature included in all plans',
  },
  FEATURE_STORAGE: {
    id: 'homepage.pricing_teaser.feature_storage',
    text: 'Unlimited storage',
    description: 'Feature included in all plans',
  },
  FEATURE_CLI: {
    id: 'homepage.pricing_teaser.feature_cli',
    text: 'Free Localingos CLI',
    description: 'Feature included in all plans',
  },
  FEATURE_API: {
    id: 'homepage.pricing_teaser.feature_api',
    text: 'Full API access',
    description: 'Feature included in all plans',
  },
  HIGHLIGHT_FREE_TRIAL: {
    id: 'homepage.pricing_teaser.highlight_free_trial',
    text: 'Free 5,000 words',
    description: 'Highlight bullet in the pricing teaser',
  },
  HIGHLIGHT_NO_CARD: {
    id: 'homepage.pricing_teaser.highlight_no_card',
    text: 'No credit card required',
    description: 'Highlight bullet in the pricing teaser',
  },
  /*
   * Was "Cancel anytime", sitting directly beside "Billed annually". Cancelling an
   * annual Stripe subscription stops the renewal; it does not refund the year, so
   * the pair read as a promise the billing cannot keep.
   */
  HIGHLIGHT_CANCEL: {
    id: 'pricing_teaser.highlight_cancel_v2',
    text: 'Cancel renewal anytime',
    description: 'Reassurance bullet on the pricing page. States that the subscription will not auto-renew if cancelled, without implying a mid-term refund.',
  },
  HIGHLIGHT_ANNUAL: {
    id: 'homepage.pricing_teaser.highlight_annual',
    text: 'Billed annually',
    description: 'Highlight bullet in the pricing teaser',
  },
  /*
   * Per-plan CTAs. The cards used to carry no button at all: a visitor could read
   * four plans, decide on one, and find nothing to click — the only action was a
   * single generic button below the grid, which discarded whichever plan they had
   * chosen. This is the page where the buying decision happens.
   */
  BTN_CHOOSE_PLAN: {
    id: 'pricing_teaser.btn_choose_plan',
    text: 'Choose {{plan}}',
    description: 'Call-to-action button on a paid plan card. {{plan}} is the plan name, e.g. "Choose Starter".',
  },
  BTN_START_FREE: {
    id: 'pricing_teaser.btn_start_free',
    text: 'Start free',
    description: 'Call-to-action button on the free plan card.',
  },
  BTN_CURRENT_PLAN: {
    id: 'pricing_teaser.btn_current_plan',
    text: 'Your current plan',
    description: 'Shown in place of a call to action on the card for the plan the signed-in visitor is already on.',
  },
  /*
   * Not a trial. TrialConfig.TRIAL_DURATION_DAYS exists but is referenced only by
   * its own test, and BillingEntitlementService is explicit that the free tier has
   * "no time limit — only usage-based expiry". Calling it a trial implies a clock,
   * and a clock is a reason to put it off until there is time to use it properly.
   * A free tier that never expires has no such reason.
   */
  BTN_START_FREE_TRIAL: {
    id: 'homepage.pricing_teaser.btn_start_free_v2',
    text: 'Start free',
    description: 'Primary call-to-action button in the pricing section. The free tier is limited by word usage, not by time, so this deliberately avoids the word "trial".',
  },
  MOST_POPULAR: {
    id: 'homepage.pricing_teaser.most_popular',
    text: 'Most Popular',
    description: 'Badge for the most popular plan',
  },
  BTN_GO_TO_DASHBOARD: {
    id: 'homepage.pricing_teaser.btn_go_to_dashboard',
    text: 'Go to Dashboard',
    description: 'CTA button shown to authenticated users on the pricing page instead of the free trial button',
  },
};

export const PricingTeaserMessages = defineMessages(PRICING_TEASER_DEFINITIONS);

/** English fallbacks, used until `npm run i18n:extract` adds these ids to en-US.json. */
export const PricingTeaserText = englishText(PRICING_TEASER_DEFINITIONS);
